import {
  CommonFieldProps,
  CommonInputProps,
  FormikCheckbox,
  FormikCombobox,
  FormikComboboxProps,
  FormikDateField,
  FormikTextField,
  FormikTextFieldProps,
  HiddenField,
  InputStyleProps
} from '../..'
import { useFieldProps } from '../../../hooks'
import { stringToBoolean } from '../../../utils'
import { CellInputType, InputCellContentProps, RowOptions } from '../types'

type CommonCellInputProps = CommonFieldProps &
  Omit<CommonInputProps, 'required'>

const inputStyleProps: InputStyleProps = {
  fullWidth: true,
  margin: 'none',
  size: 'small',
  variant: 'outlined'
}

const parseFieldName = <T,>(rowName: string, key: keyof T) =>
  rowName + '.' + key

export const InputContent = <RowDataType,>({
  cellProps,
  row,
  index,
  isMobile,
  tableName
}: InputCellContentProps<RowDataType>) => {
  const { key, inputTypeProps, label, labelTranslationId } = cellProps
  const rowName = tableName + '.' + index
  const name = parseFieldName(rowName, key)
  const inputType = inputTypeProps?.type
  const inputKey = inputTypeProps?.inputKey

  const inputProps = inputTypeProps?.inputProps
  const inputLabel = inputProps?.label
  const inputLabelTranslationId = inputProps?.labelTranslationId

  let commonInputProps: CommonCellInputProps = {
    fontSize: isMobile ? undefined : '14px',
    label: isMobile ? inputLabel || label : inputLabel,
    labelTranslationId: isMobile
      ? inputLabelTranslationId || labelTranslationId
      : inputLabelTranslationId,
    name
  }

  if (inputType !== CellInputType.CheckBox) {
    commonInputProps = {
      ...inputStyleProps,
      ...commonInputProps,
      placeholder: inputTypeProps?.inputProps?.placeholder,
      placeholderTranslationId:
        inputTypeProps?.inputProps?.placeholderTranslationId
    }
  }

  if (inputType === CellInputType.Text) {
    return <TextInputContent {...commonInputProps} />
  } else if (inputType === CellInputType.Date) {
    return <FormikDateField {...commonInputProps} gridProps={{ md: 11 }} />
  } else if (inputType === CellInputType.CheckBox) {
    const hiddenFieldName = inputKey
      ? parseFieldName(rowName, inputKey)
      : undefined
    return (
      <FormikCheckbox {...commonInputProps} name={hiddenFieldName || name} />
    )
  } else if (inputType === CellInputType.ComboBox) {
    const rowOptions = row.rowOptions?.find(
      (option: RowOptions<RowDataType>) => option.key === key
    )
    const options = rowOptions?.options

    if (options && options.length > 0) {
      const selectProps = {
        ...commonInputProps,
        options
      }

      if (inputKey) {
        return (
          <SelectInputContentWithHiddenField
            {...selectProps}
            hiddenFieldName={parseFieldName(rowName, inputKey)}
          />
        )
      } else {
        return <SelectInputContent {...selectProps} />
      }
    } else {
      return <TextInputContent {...commonInputProps} />
    }
  }
  return null
}

type SelectInputContentProps = Omit<FormikComboboxProps, 'gridProps'>

const SelectInputContent = ({ ...props }: SelectInputContentProps) => {
  return <FormikCombobox {...props} gridProps={{ md: 11 }} />
}

interface SelectInputContentWithHiddenFieldProps
  extends SelectInputContentProps {
  hiddenFieldName: string
}

export const SelectInputContentWithHiddenField = ({
  hiddenFieldName,
  ...selectProps
}: SelectInputContentWithHiddenFieldProps) => {
  const { setValue: setHiddenFieldValue } = useFieldProps(hiddenFieldName)

  const executeOnChange = (selectedValue: string) => {
    const hiddenFieldValue = stringToBoolean(selectedValue)
    setHiddenFieldValue(hiddenFieldValue)
  }

  return (
    <>
      <SelectInputContent
        {...selectProps}
        executeOnChangeFn={executeOnChange}
      />
      <HiddenField name={hiddenFieldName} />
    </>
  )
}

type TextInputContentProps = Omit<FormikTextFieldProps, 'gridProps'>

export const TextInputContent = ({ ...props }: TextInputContentProps) => {
  return <FormikTextField {...props} gridProps={{ md: 11 }} />
}
